import { supabase } from './supabase';

// Database types matching Supabase schema
export interface DbContract {
    id: string;
    user_id: string;
    title: string;
    storage_path?: string;
    status: 'analyzing' | 'completed' | 'failed';
    policy_id?: string;
    summary?: string;
    created_at: string;
}

export interface DbContractRisk {
    id: string;
    contract_id: string;
    original_text: string;
    risk_level: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
    description: string;
    suggestion: string;
    revised_text?: string;
    created_at: string;
}

export interface DbAnalysisStep {
    id: string;
    contract_id: string;
    step_number: number;
    step_name: string;
    status: 'pending' | 'running' | 'completed' | 'failed';
    logs?: { thoughts?: string[]; duration?: number };
    created_at: string;
}

// Full contract with relations
export interface ContractWithDetails extends DbContract {
    risks?: DbContractRisk[];
    steps?: DbAnalysisStep[];
}

export const contractService = {
    // Create a new contract record
    async createContract(title: string, policyId?: string): Promise<DbContract | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not logged in');

        const { data, error } = await supabase
            .from('contracts')
            .insert({
                user_id: user.id,
                title,
                status: 'analyzing',
                policy_id: policyId
            })
            .select();

        if (error) throw error;
        return data?.[0] || null;
    },

    // Update contract status and summary
    async updateContract(
        contractId: string,
        updates: { status?: string; summary?: string; storage_path?: string }
    ): Promise<DbContract | null> {
        const { data, error } = await supabase
            .from('contracts')
            .update(updates)
            .eq('id', contractId)
            .select();

        if (error) throw error;
        return data?.[0] || null;
    },

    // Save parsed content (fullContent and displayHtml) to storage and localStorage
    async saveParsedContent(contractId: string, fullContent: string[], displayHtml: string): Promise<string | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not logged in');

        const contentData = {
            fullContent,
            displayHtml,
            savedAt: new Date().toISOString()
        };

        // 总是保存到 localStorage 作为主要存储方式（不受 RLS 限制）
        try {
            const storageKey = `contract_content_${user.id}_${contractId}`;
            localStorage.setItem(storageKey, JSON.stringify(contentData));
            console.log(`[contractService] ✓ 已保存解析内容到 localStorage: ${storageKey}`);
        } catch (localStorageError) {
            console.warn('Error saving to localStorage:', localStorageError);
        }

        // 尝试保存到 Supabase Storage（可选，如果失败也不影响）
        try {
            const contentJson = JSON.stringify(contentData);
            const contentBlob = new Blob([contentJson], { type: 'application/json' });
            const contentPath = `${user.id}/${contractId}/parsed_content.json`;

            const { error } = await supabase.storage
                .from('contracts')
                .upload(contentPath, contentBlob, {
                    upsert: true,
                    contentType: 'application/json'
                });

            if (!error) {
                console.log(`[contractService] ✓ 也保存到 Supabase Storage: ${contentPath}`);
                return contentPath;
            } else {
                console.warn(`[contractService] Supabase Storage 保存失败（已保存到 localStorage）:`, error.message);
            }
        } catch (storageError) {
            console.warn('Error saving to Supabase Storage (content is in localStorage):', storageError);
        }

        // 即使 Storage 失败，也返回成功（因为 localStorage 已保存）
        return 'localStorage';
    },

    // Load parsed content from storage and localStorage
    async loadParsedContent(contractId: string): Promise<{ fullContent: string[]; displayHtml: string } | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        // 首先尝试从 localStorage 加载（更快，不受 RLS 限制）
        try {
            const storageKey = `contract_content_${user.id}_${contractId}`;
            const localData = localStorage.getItem(storageKey);
            if (localData) {
                const contentData = JSON.parse(localData);
                console.log(`[contractService] ✓ 从 localStorage 加载解析内容`);
                return {
                    fullContent: contentData.fullContent || [],
                    displayHtml: contentData.displayHtml || ''
                };
            }
        } catch (localStorageError) {
            console.warn('Error loading from localStorage:', localStorageError);
        }

        // 如果 localStorage 没有，尝试从 Supabase Storage 加载
        const contentPath = `${user.id}/${contractId}/parsed_content.json`;
        const { data, error } = await supabase.storage
            .from('contracts')
            .download(contentPath);

        if (error || !data) {
            console.log('Parsed content not found in Storage:', contentPath);
            return null;
        }

        try {
            const text = await data.text();
            const contentData = JSON.parse(text);
            console.log(`[contractService] ✓ 从 Supabase Storage 加载解析内容`);
            // 同时保存到 localStorage 以便下次快速访问
            try {
                const storageKey = `contract_content_${user.id}_${contractId}`;
                localStorage.setItem(storageKey, text);
            } catch (e) {
                // 忽略 localStorage 错误
            }
            return {
                fullContent: contentData.fullContent || [],
                displayHtml: contentData.displayHtml || ''
            };
        } catch (err) {
            console.error('Error parsing content JSON:', err);
            return null;
        }
    },

    // Get all contracts for current user
    async getUserContracts(): Promise<DbContract[]> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];

        const { data, error } = await supabase
            .from('contracts')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching contracts:', error);
            return [];
        }
        return data || [];
    },

    // Get a single contract with its risks and steps
    async getContractWithDetails(contractId: string): Promise<ContractWithDetails | null> {
        const { data: contractData, error: contractError } = await supabase
            .from('contracts')
            .select('*')
            .eq('id', contractId);

        if (contractError) {
            console.error('Error fetching contract:', contractError);
            return null;
        }

        const contract = contractData?.[0];
        if (!contract) return null;

        // Fetch risks
        const { data: risks } = await supabase
            .from('contract_risks')
            .select('*')
            .eq('contract_id', contractId)
            .order('created_at', { ascending: true });

        // Fetch analysis steps
        const { data: steps } = await supabase
            .from('analysis_steps')
            .select('*')
            .eq('contract_id', contractId)
            .order('step_number', { ascending: true });

        return {
            ...contract,
            risks: risks || [],
            steps: steps || []
        };
    },

    // Save analysis risks
    async saveRisks(contractId: string, risks: Omit<DbContractRisk, 'id' | 'contract_id' | 'created_at'>[]): Promise<void> {
        if (risks.length === 0) return;

        const risksToInsert = risks.map(risk => ({
            contract_id: contractId,
            ...risk
        }));

        const { error } = await supabase
            .from('contract_risks')
            .insert(risksToInsert);

        if (error) throw error;
    },

    // Create or update an analysis step
    async upsertAnalysisStep(
        contractId: string,
        stepNumber: number,
        stepName: string,
        status: string,
        logs?: { thoughts?: string[]; duration?: number }
    ): Promise<void> {
        // Check if step exists
        const { data: existingData } = await supabase
            .from('analysis_steps')
            .select('id')
            .eq('contract_id', contractId)
            .eq('step_number', stepNumber);

        const existing = existingData?.[0];

        if (existing) {
            // Update
            const { error } = await supabase
                .from('analysis_steps')
                .update({ status, logs })
                .eq('id', existing.id);
            if (error) throw error;
        } else {
            // Insert
            const { error } = await supabase
                .from('analysis_steps')
                .insert({
                    contract_id: contractId,
                    step_number: stepNumber,
                    step_name: stepName,
                    status,
                    logs
                });
            if (error) throw error;
        }
    },

    // Delete a contract and all related data (cascades via DB)
    async deleteContract(contractId: string): Promise<void> {
        // First delete related risks and steps (if no cascade)
        await supabase.from('contract_risks').delete().eq('contract_id', contractId);
        await supabase.from('analysis_steps').delete().eq('contract_id', contractId);

        const { error } = await supabase
            .from('contracts')
            .delete()
            .eq('id', contractId);

        if (error) throw error;
    },

    // Upload contract file to Supabase Storage
    async uploadContractFile(contractId: string, file: File): Promise<string | null> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not logged in');

        // 对文件名进行安全处理：使用时间戳 + 原始文件名哈希，避免中文字符和特殊字符问题
        const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
        const timestamp = Date.now();
        // 修复 hash 计算，确保不会返回 NaN
        let hash = 0;
        for (let i = 0; i < file.name.length; i++) {
            const char = file.name.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        const hashStr = Math.abs(hash).toString(36);
        
        // 使用安全的文件名：timestamp_hash.extension
        const safeFileName = `${timestamp}_${hashStr}${fileExtension}`;
        const filePath = `${user.id}/${contractId}/${safeFileName}`;

        console.log(`[contractService] 上传文件: ${file.name} -> ${filePath}`);

        const { error } = await supabase.storage
            .from('contracts')
            .upload(filePath, file, {
                upsert: true,
                contentType: file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });

        if (error) {
            console.warn('Error uploading file (will use localStorage as fallback):', error.message);
            // 如果上传失败，不抛出错误，而是返回 null
            // 内容会保存在 localStorage 中作为备用
            return null;
        }

        return filePath;
    },

    // Download contract file from Supabase Storage
    async downloadContractFile(path: string): Promise<Blob | null> {
        try {
            const originalPath = path;
            const pathsToTry: string[] = [originalPath];
            
            // 如果路径包含未编码的中文字符，尝试编码文件名部分
            const pathParts = path.split('/');
            if (pathParts.length >= 3) {
                const fileName = pathParts[pathParts.length - 1];
                const lastDotIndex = fileName.lastIndexOf('.');
                
                if (lastDotIndex > 0) {
                    const fileNameWithoutExt = fileName.substring(0, lastDotIndex);
                    const fileExtension = fileName.substring(lastDotIndex);
                    
                    // 检查文件名是否包含中文字符或特殊字符，且未编码
                    const hasSpecialChars = /[^\x00-\x7F]/.test(fileNameWithoutExt);
                    const isEncoded = fileNameWithoutExt.includes('%');
                    
                    if (hasSpecialChars && !isEncoded) {
                        // 文件名未编码，需要编码
                        const encodedFileName = encodeURIComponent(fileNameWithoutExt) + fileExtension;
                        pathParts[pathParts.length - 1] = encodedFileName;
                        pathsToTry.push(pathParts.join('/'));
                        
                        // 也尝试只编码中文字符部分
                        const partiallyEncoded = fileNameWithoutExt.replace(/[\u4e00-\u9fa5]/g, (char) => {
                            return encodeURIComponent(char);
                        }) + fileExtension;
                        pathParts[pathParts.length - 1] = partiallyEncoded;
                        pathsToTry.push(pathParts.join('/'));
                    } else if (isEncoded) {
                        // 如果已经编码，尝试解码后重新编码
                        try {
                            const decoded = decodeURIComponent(fileNameWithoutExt);
                            const reEncoded = encodeURIComponent(decoded) + fileExtension;
                            pathParts[pathParts.length - 1] = reEncoded;
                            pathsToTry.push(pathParts.join('/'));
                        } catch (e) {
                            // 解码失败，忽略
                        }
                    }
                }
            }

            // 尝试所有可能的路径变体
            for (const tryPath of pathsToTry) {
                console.log(`[contractService] 尝试下载路径: ${tryPath}`);
                const { data, error } = await supabase.storage
                    .from('contracts')
                    .download(tryPath);

                if (!error && data) {
                    console.log(`[contractService] 成功下载文件: ${tryPath}`);
                    return data;
                } else if (error) {
                    console.warn(`[contractService] 路径 ${tryPath} 下载失败:`, error.message);
                }
            }

            // 如果所有路径都失败，尝试列出目录中的文件来找到正确的文件名
            if (pathParts.length >= 2) {
                const directory = pathParts.slice(0, -1).join('/');
                console.log(`[contractService] 尝试列出目录中的文件: ${directory}`);
                const { data: files, error: listError } = await supabase.storage
                    .from('contracts')
                    .list(directory);

                if (listError) {
                    console.warn(`[contractService] 列出目录失败:`, listError.message || listError);
                } else if (files) {
                    if (files.length === 0) {
                        console.log(`[contractService] 目录为空: ${directory}`);
                    } else {
                        console.log(`[contractService] 目录中找到 ${files.length} 个文件:`, files.map(f => f.name));
                        // 尝试匹配文件名（忽略扩展名和编码）
                        const targetName = pathParts[pathParts.length - 1].toLowerCase();
                        const matchingFile = files.find(f => {
                            const fileName = f.name.toLowerCase();
                            try {
                                return fileName === targetName || 
                                       fileName.replace(/%[0-9A-F]{2}/gi, '') === targetName.replace(/%[0-9A-F]{2}/gi, '') ||
                                       decodeURIComponent(fileName) === decodeURIComponent(targetName);
                            } catch (e) {
                                // 解码失败，只比较原始文件名
                                return fileName === targetName;
                            }
                        });

                        if (matchingFile) {
                            const correctPath = `${directory}/${matchingFile.name}`;
                            console.log(`[contractService] 找到匹配的文件，尝试下载: ${correctPath}`);
                            const { data: correctData, error: correctError } = await supabase.storage
                                .from('contracts')
                                .download(correctPath);
                            
                            if (!correctError && correctData) {
                                console.log(`[contractService] 成功下载匹配的文件: ${correctPath}`);
                                return correctData;
                            } else if (correctError) {
                                console.warn(`[contractService] 下载匹配文件失败:`, correctError.message || correctError);
                            }
                        } else {
                            console.log(`[contractService] 目录中没有找到匹配的文件，目标文件名: ${targetName}`);
                        }

                        // 最后兜底：上传时用 timestamp_hash.docx，若 DB 存的是原名会不匹配。直接取目录下第一个 .docx
                        const docx = files.find(f => !f.name.startsWith('.') && /\.docx?$/i.test(f.name));
                        if (docx) {
                            const fallbackPath = `${directory}/${docx.name}`;
                            console.log(`[contractService] 兜底尝试下载目录内 .docx: ${fallbackPath}`);
                            const { data: fallbackData, error: fallbackErr } = await supabase.storage
                                .from('contracts')
                                .download(fallbackPath);
                            if (!fallbackErr && fallbackData) {
                                console.log(`[contractService] 兜底下载成功: ${fallbackPath}`);
                                return fallbackData;
                            }
                            if (fallbackErr) console.warn(`[contractService] 兜底下载失败:`, fallbackErr.message);
                        }
                    }
                }
            }

            console.error('[contractService] 所有下载尝试都失败了');
            return null;
        } catch (err) {
            console.error('Error in downloadContractFile:', err);
            return null;
        }
    }
};
